import React, { Component } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import i18n from '../../../i18n/i18n';
import './SettingsPage.css';

// Import assets
import { ReactComponent as ArrowRightIcon } from '../../../assets/icons/arrow-right.svg';

// Import Redux actions
import GetMeActions from '../../../redux/GetMeRedux';
import UpdateUserLocaleActions from '../../../redux/UpdateUserLocaleRedux';
import UpdateUserNotificationSettingsActions from '../../../redux/UpdateUserNotificationSettingsRedux';
import LogoutActions from '../../../redux/LogoutRedux';

interface SettingsPageProps extends WithTranslation {
  navigate: NavigateFunction;
  
  // Redux props
  userData?: any;
  currentLanguage?: string;
  notificationSettings?: any;
  children?: any[];
  
  // Redux actions
  getMe: (accessToken: string, navigate: any) => void;
  updateUserLocale: (locale: string) => void;
  updateUserNotificationSettings: (notificationType: string, status: boolean) => void;
  logout: () => void;
  
  // Redux state
  getMeFetching?: boolean;
  getMeSuccess?: boolean;
  getMeError?: any;
  updateLocaleFetching?: boolean;
  updateNotificationFetching?: boolean;
  logoutFetching?: boolean;
}

interface SettingsPageState {
  loading: boolean;
  selectedLanguage: string;
  showLanguageDropdown: boolean;
  localNotificationSettings: {
    goals: boolean;
    announcements: boolean;
    appointments: boolean;
  };
  notificationFetched: boolean;
}

class SettingsPage extends Component<SettingsPageProps, SettingsPageState> {
  constructor(props: SettingsPageProps) {
    super(props);
    
    // Get current language from i18n as the source of truth
    const currentI18nLanguage = i18n.language || 'en';
    
    this.state = {
      loading: false,
      selectedLanguage: props.currentLanguage || currentI18nLanguage, // Use i18n language if Redux is not available
      showLanguageDropdown: false,
      localNotificationSettings: {
        goals: true,
        announcements: true,
        appointments: true,
      },
      notificationFetched: false,
    };
  }

  /* static getDerivedStateFromProps(nextProps: Readonly<SettingsPageProps>, prevState: Readonly<SettingsPageState>) {
    const updates: Partial<SettingsPageState> = {};
    
    // Sync language - prioritize Redux, fallback to i18n current language
    // const actualLanguage = nextProps.currentLanguage || i18n.language || 'en';
    // if (actualLanguage !== prevState.selectedLanguage) {
    //   updates.selectedLanguage = actualLanguage;
    // }

    console.log('getDerivedStateFromProps: nextProps.notificationSettings:', nextProps, 'prevState.localNotificationSettings:', prevState);
   
    // Sync notification settings if Redux has them and they're different from local state
    if (nextProps.notificationSettings) {
      const newSettings = {
        goals: nextProps.notificationSettings.goals !== undefined ? nextProps.notificationSettings.goals : true,
        announcements: nextProps.notificationSettings.announcements !== undefined ? nextProps.notificationSettings.announcements : true,
        appointments: nextProps.notificationSettings.appointments !== undefined ? nextProps.notificationSettings.appointments : true,
      };
      
      if (JSON.stringify(newSettings) !== JSON.stringify(prevState.localNotificationSettings)) {
        updates.localNotificationSettings = newSettings;
      }
    }
    
    return Object.keys(updates).length > 0 ? updates : null;
  } */
  

  componentDidMount() {
    // Load user data to get current settings and language
    this.props.getMe(localStorage.getItem('accessToken') || '', this.props.navigate);
    
    // Initialize state with any existing Redux data
    this._syncStateWithRedux();
  }

  componentDidUpdate(prevProps: SettingsPageProps) {
    // Update local language state when Redux language changes
    if (prevProps.currentLanguage !== this.props.currentLanguage && this.props.currentLanguage) {
      this.setState({ selectedLanguage: this.props.currentLanguage });
    }

    console.log('componentDidUpdate: prevProps.notificationSettings:', prevProps.notificationSettings, 'this.props.notificationSettings:', this.props.notificationSettings);

    // Update local notification settings when Redux settings change
    if (!this.state.notificationFetched && prevProps.notificationSettings !== this.props.notificationSettings && this.props.notificationSettings) {
      const newSettings = {
        goals: this.props.notificationSettings.goals !== undefined ? this.props.notificationSettings.goals : true,
        announcements: this.props.notificationSettings.announcements !== undefined ? this.props.notificationSettings.announcements : true,
        appointments: this.props.notificationSettings.appointments !== undefined ? this.props.notificationSettings.appointments : true,
      };
      
      this.setState({ localNotificationSettings: newSettings, notificationFetched: true });
    }

    // Handle locale update response
    if (prevProps.updateLocaleFetching !== this.props.updateLocaleFetching && !this.props.updateLocaleFetching) {
      // Force sync local state with Redux if there's a mismatch
      if (this.props.currentLanguage && this.props.currentLanguage !== this.state.selectedLanguage) {
        this.setState({ selectedLanguage: this.props.currentLanguage });
      }
    }

    // Handle notification update response
    if (prevProps.updateNotificationFetching !== this.props.updateNotificationFetching && !this.props.updateNotificationFetching) {
      // Notification settings updated successfully
    }

    // Handle logout response
    if (prevProps.logoutFetching !== this.props.logoutFetching && !this.props.logoutFetching) {
      this.setState({ loading: false });
      // Redirect to login after successful logout
      // this.props.navigate('/login');
      this.props.navigate('/tutorial');
    }
  }

  _syncStateWithRedux = () => {
    const { currentLanguage, notificationSettings } = this.props;
    
    // Sync language - use Redux if available, otherwise use i18n current language
    const actualLanguage = currentLanguage || i18n.language || 'en';
    if (actualLanguage !== this.state.selectedLanguage) {
      this.setState({ selectedLanguage: actualLanguage });
    }

    console.log('syncStateWithRedux: notificationSettings:', notificationSettings, 'this.state.localNotificationSettings:', this.state.localNotificationSettings);
    
    // Sync notification settings
    if (notificationSettings) {
      const newSettings = {
        goals: notificationSettings.goals !== undefined ? notificationSettings.goals : true,
        announcements: notificationSettings.announcements !== undefined ? notificationSettings.announcements : true,
        appointments: notificationSettings.appointments !== undefined ? notificationSettings.appointments : true,
      };
      
      // Only update if different from current local state
      if (JSON.stringify(newSettings) !== JSON.stringify(this.state.localNotificationSettings)) {
        this.setState({ localNotificationSettings: newSettings });
      }
    }
  };

  _onBackPress = () => {
    this.props.navigate(-1);
  };

  // Navigation methods

  _onNavigateToChangePassword = () => {
    this.props.navigate('/settings/change-password');
  };

  _onNavigateToProfile = () => {
    this.props.navigate('/settings/profile');
  };

  _onNavigateToAppDescription = () => {
    this.props.navigate('/settings/app-description');
  };

  _onNavigateToPrivacyPolicy = () => {
    this.props.navigate('/settings/privacy');
  };

  _onNavigateToTermsAndConditions = () => {
    this.props.navigate('/settings/terms');
  };

  _onNavigateToDisclaimers = () => {
    this.props.navigate('/settings/disclaimers');
  };

  _onNavigateToContactUs = () => {
    // Simple contact functionality - following reference project
    window.open('mailto:info@cdchk.org', '_blank');
  };

  _onNavigateToDonateNow = () => {
    // Simple donation functionality - following reference project
    window.open('https://cdchk.org', '_blank');
  };

  _onNavigateToFacebook = () => {
    // Facebook link - following reference project
    window.open('https://facebook.com/cdchk', '_blank');
  };

  _onNavigateToOnBoarding = () => {
    this.props.navigate('/settings/onboarding');
  };

  _onSignOut = () => {
    // Validation before sign out
    if (this.state.loading) {
      return; // Prevent multiple sign out attempts
    }
    
    this.setState({ loading: true });
    this.props.logout();
  };

  // Language and notification methods - OPTIMIZED
  _onLanguageChange = (language: string) => {
    // Validation
    if (!this._validateLanguage(language)) {
      console.error('Invalid language:', language);
      return;
    }
    
    // Prevent duplicate API calls if same language is selected
    if (language === this.state.selectedLanguage) {
      return;
    }
    
    // Update local state immediately for better UX
    this.setState({ selectedLanguage: language });

    console.log('SettingsPage: Language changed to:', language);
    
    // Update the UI language using i18n
    i18n.changeLanguage(language);
    
    // Call API to update server and Redux - SINGLE API CALL
    this.props.updateUserLocale(language);
  };

  _onNotificationToggle = (type: string, enabled: boolean) => {
    // Validation
    if (!this._validateNotificationType(type)) {
      console.error('Invalid notification type:', type);
      return;
    }
    
    // Update local state immediately for better UX
    this.setState(prevState => {
      console.log('SettingsPage: Notification toggle:', type, enabled);
      const newSettings = {
        ...prevState.localNotificationSettings,
        [type]: enabled,
      };
      return { localNotificationSettings: newSettings };
    });
    
    // Call API to update server
    this.props.updateUserNotificationSettings(type, enabled);
  };

  _onRetryLoadData = () => {
    // No longer needed without preload
  };

  _onDismissError = () => {
    // No longer needed without preload
  };

  _onDismissSuccess = () => {
    // No longer needed without preload
  };

  // Advanced Features - Phase 7
  _onKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  _onFocus = (event: React.FocusEvent) => {
    event.currentTarget.classList.add('focused');
  };

  _onBlur = (event: React.FocusEvent) => {
    event.currentTarget.classList.remove('focused');
  };

  _onLanguageKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const select = event.currentTarget as HTMLSelectElement;
      const currentIndex = select.selectedIndex;
      const newIndex = event.key === 'ArrowDown' 
        ? Math.min(currentIndex + 1, select.options.length - 1)
        : Math.max(currentIndex - 1, 0);
      select.selectedIndex = newIndex;
      this._onLanguageChange(select.options[newIndex].value);
    }
  };

  _onToggleKeyDown = (event: React.KeyboardEvent, type: string, currentValue: boolean) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this._onNotificationToggle(type, !currentValue);
    }
  };

  // Testing & Validation - Phase 8
  _validateLanguage = (language: string): boolean => {
    return ['en', 'zh'].includes(language);
  };

  _validateNotificationType = (type: string): boolean => {
    return ['goals', 'announcements', 'appointments'].includes(type);
  };

  // Performance Optimization - Phase 9 - ENHANCED
  shouldComponentUpdate(nextProps: SettingsPageProps, nextState: SettingsPageState): boolean {
    // Only update if relevant props/state changed
    const shouldUpdate = (
      this.props.currentLanguage !== nextProps.currentLanguage ||
      this.props.notificationSettings !== nextProps.notificationSettings ||
      this.props.getMeFetching !== nextProps.getMeFetching ||
      this.props.updateLocaleFetching !== nextProps.updateLocaleFetching ||
      this.props.updateNotificationFetching !== nextProps.updateNotificationFetching ||
      this.props.logoutFetching !== nextProps.logoutFetching ||
      this.state.selectedLanguage !== nextState.selectedLanguage ||
      this.state.loading !== nextState.loading ||
      this.state.localNotificationSettings !== nextState.localNotificationSettings
    );
    
    // Debug logging for performance monitoring
    if (shouldUpdate) {
      
    }
    
    return shouldUpdate;
  }

  /* _renderLanguageOptions = () => (
    <>
      <option value="en">English</option>
      <option value="zh">中文</option>
    </>
  ); */

  _renderLoadingSpinner = () => {
    const { getMeFetching, updateLocaleFetching, updateNotificationFetching, logoutFetching, t } = this.props;
    const { loading } = this.state;
    const isLoading = logoutFetching ; 
    // getMeFetching || updateLocaleFetching || updateNotificationFetching || loading || 
    
    if (!isLoading) return null;
    
    return (
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
        <div className="loading-text">
          {/* {getMeFetching && t('common:loading')} */}
          {/* updateLocaleFetching && t('common:updating') */}
          {/* updateNotificationFetching && t('common:updating') */}
          {logoutFetching && t('common:signing-out')}
          {/* {loading && t('common:loading')} */}
        </div>
      </div>
    );
  };



  // Language key mapping utilities - OPTIMIZED
  _mapFrontendToServer = (frontendLocale: string): string => {
    switch (frontendLocale) {
      case 'zh':
        return 'hk'; // Server expects 'hk' for Chinese
      case 'en':
        return 'en'; // Server expects 'en' for English
      default:
        console.warn('Unknown frontend locale, using as-is:', frontendLocale);
        return frontendLocale;
    }
  };

  _mapServerToFrontend = (serverLocale: string): string => {
    switch (serverLocale) {
      case 'hk':
        return 'zh'; // Server returns 'hk', frontend needs 'zh'
      case 'en':
        return 'en'; // Server returns 'en', frontend needs 'en'
      default:
        console.warn('Unknown server locale, using as-is:', serverLocale);
        return serverLocale;
    }
  };

  // Notification key mapping utilities
  _mapServerNotificationKeys = (serverSettings: any) => {
    if (!serverSettings) return null;
    
    // Map server keys to frontend keys
    // Don't default to true - use the actual server values
    return {
      goals: serverSettings.goal_notifications !== undefined ? serverSettings.goal_notifications : true,
      announcements: serverSettings.announcement_notifications !== undefined ? serverSettings.announcement_notifications : true,
      appointments: serverSettings.appointment_notifications !== undefined ? serverSettings.appointment_notifications : true,
    };
  };

  _mapFrontendNotificationKeys = (frontendSettings: any) => {
    if (!frontendSettings) return null;
    
    // Map frontend keys to server keys
    return {
      goal_notifications: frontendSettings.goals,
      announcement_notifications: frontendSettings.announcements,
      appointment_notifications: frontendSettings.appointments,
    };
  };







  render() {
    const { 
      loading, 
      selectedLanguage, 
      showLanguageDropdown,
      localNotificationSettings
    } = this.state;
    
    const { 
      t, 
      getMeFetching, 
      getMeSuccess, 
      getMeError,
      updateLocaleFetching,
      updateNotificationFetching,
      logoutFetching,
      notificationSettings
    } = this.props;

    const isLoading = getMeFetching || updateLocaleFetching || updateNotificationFetching || logoutFetching || loading;
    const isDataLoaded = getMeSuccess && notificationSettings;

    return (
      <div className="settings-page">
        {/* Header Section */}
        <div className="page-header">
          <div className="page-header-inner">
            <div className="header-left">
              <h1 className="header-title">{t('settings-screen:title')}</h1>
            </div>
          </div>
        </div>

        {/* Settings Content */}
        <div className="settings-content">


          {/* Profile to Sign Out Section */}
          <div className="settings-section">
            <h2 className="section-title">{t('settings-screen:section.header.account-settings')}</h2>

            {/*<div className="settings-item" 
              onClick={this._onNavigateToProfile}
              onKeyDown={(e) => this._onKeyDown(e, this._onNavigateToProfile)}
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              tabIndex={0}
              role="button"
              aria-label={t('settings-screen:section.header.item.profile')}
            >
              <div className="item-left">
                <span className="item-text">{t('settings-screen:section.header.item.profile')}</span>
              </div>
              <ArrowRightIcon className="arrow-icon" />
            </div>
            */}

            <div className="settings-item sign-out-item" 
              onClick={this._onSignOut}
              onKeyDown={(e) => this._onKeyDown(e, this._onSignOut)}
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              tabIndex={0}
              role="button"
              aria-label={t('settings-screen:section.header.item.sign-out')}
            >
              <div className="item-left">
                <span className="item-text sign-out-text">{t('settings-screen:section.header.item.sign-out')}</span>
              </div>
              <ArrowRightIcon className="arrow-icon" />
            </div>
            
            <div className="settings-item" 
              onClick={this._onNavigateToChangePassword}
              onKeyDown={(e) => this._onKeyDown(e, this._onNavigateToChangePassword)}
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              tabIndex={0}
              role="button"
              aria-label={t('settings-screen:section.header.item.change-password')}
            >
              <div className="item-left">
                <span className="item-text">{t('settings-screen:section.header.item.change-password')}</span>
              </div>
              <ArrowRightIcon className="arrow-icon" />
            </div>

            

            
          </div>

          {/* Language Section */}
          <div className="settings-section">
            <h2 className="section-title">{t('settings-screen:section.header.language')}</h2>
            
            
              <>
                <div className="settings-item" tabIndex={0} onClick={() => this._onLanguageChange('en')}>
                  <div className="item-left">
                    <span className="item-text">
                      English
                    </span>
                  </div>
                  { (selectedLanguage === 'en' || (!selectedLanguage && i18n.language === 'en')) && 
                    <span className="checkmark"></span>
                  }
                </div>
                <div className="settings-item" tabIndex={0} onClick={() => this._onLanguageChange('zh')}>
                  <div className="item-left">
                    <span className="item-text">
                      中文
                    </span>
                  </div>
                  { (selectedLanguage === 'zh' || (!selectedLanguage && i18n.language === 'zh')) && 
                    <span className="checkmark"></span>
                  }
                </div>
              </>
            
          </div>

          {/* Notifications Section */}
          <div className="settings-section">
            <h2 className="section-title">{t('settings-screen:section.header.notifications')}</h2>
            
            
              <>
                <div className="settings-item" tabIndex={0}>
                  <div className="item-left">
                    <span className="item-text">{t('settings-screen:section.header.item.goals-notifications')}</span>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={localNotificationSettings.goals}
                      onChange={(e) => this._onNotificationToggle('goals', e.target.checked)}
                      disabled={updateNotificationFetching}
                      onKeyDown={(e) => this._onToggleKeyDown(e, 'goals', localNotificationSettings.goals)}
                      onFocus={this._onFocus}
                      onBlur={this._onBlur}
                      aria-label={t('settings-screen:section.header.item.goals-notifications')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="settings-item" tabIndex={0}>
                  <div className="item-left">
                    <span className="item-text">{t('settings-screen:section.header.item.appointment-notifications')}</span>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={localNotificationSettings.appointments}
                      onChange={(e) => this._onNotificationToggle('appointments', e.target.checked)}
                      disabled={updateNotificationFetching}
                      onKeyDown={(e) => this._onToggleKeyDown(e, 'appointments', localNotificationSettings.appointments)}
                      onFocus={this._onFocus}
                      onBlur={this._onBlur}
                      aria-label={t('settings-screen:section.header.item.appointment-notifications')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="settings-item" tabIndex={0}>
                  <div className="item-left">
                    <span className="item-text">{t('settings-screen:section.header.item.announcement-notifications')}</span>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={localNotificationSettings.announcements}
                      onChange={(e) => this._onNotificationToggle('announcements', e.target.checked)}
                      disabled={updateNotificationFetching}
                      onKeyDown={(e) => this._onToggleKeyDown(e, 'announcements', localNotificationSettings.announcements)}
                      onFocus={this._onFocus}
                      onBlur={this._onBlur}
                      aria-label={t('settings-screen:section.header.item.announcement-notifications')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                
              </>
            
          </div>
          
          {/* Information Section */}
          <div className="settings-section">
            <h2 className="section-title">{t('settings-screen:section.header.information')}</h2>
            
            <div className="settings-item" 
              onClick={this._onNavigateToOnBoarding}
              onKeyDown={(e) => this._onKeyDown(e, this._onNavigateToOnBoarding)}
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              tabIndex={0}
              role="button"
              aria-label={t('settings-screen:section.header.item.on-boarding')}
            >
              <div className="item-left">
                <span className="item-text">{t('settings-screen:section.header.item.on-boarding')}</span>
              </div>
              <ArrowRightIcon className="arrow-icon" />
            </div>

            <div className="settings-item" 
              onClick={this._onNavigateToTermsAndConditions}
              onKeyDown={(e) => this._onKeyDown(e, this._onNavigateToTermsAndConditions)}
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              tabIndex={0}
              role="button"
              aria-label={t('settings-screen:section.header.item.terms-and-condition')}
            >
              <div className="item-left">
                <span className="item-text">{t('settings-screen:section.header.item.terms-and-condition')}</span>
              </div>
              <ArrowRightIcon className="arrow-icon" />
            </div>

            <div className="settings-item" 
              onClick={this._onNavigateToPrivacyPolicy}
              onKeyDown={(e) => this._onKeyDown(e, this._onNavigateToPrivacyPolicy)}
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              tabIndex={0}
              role="button"
              aria-label={t('settings-screen:section.header.item.privacy-policy')}
            >
              <div className="item-left">
                <span className="item-text">{t('settings-screen:section.header.item.privacy-policy')}</span>
              </div>
              <ArrowRightIcon className="arrow-icon" />
            </div>

            <div className="settings-item" 
              onClick={this._onNavigateToDisclaimers}
              onKeyDown={(e) => this._onKeyDown(e, this._onNavigateToDisclaimers)}
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              tabIndex={0}
              role="button"
              aria-label={t('settings-screen:section.header.item.disclaimers')}
            >
              <div className="item-left">
                <span className="item-text">{t('settings-screen:section.header.item.disclaimers')}</span>
              </div>
              <ArrowRightIcon className="arrow-icon" />
            </div>

            <div className="settings-item" 
              onClick={this._onNavigateToAppDescription}
              onKeyDown={(e) => this._onKeyDown(e, this._onNavigateToAppDescription)}
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              tabIndex={0}
              role="button"
              aria-label={t('settings-screen:section.header.item.app-description')}
            >
              <div className="item-left">
                <span className="item-text">{t('settings-screen:section.header.item.app-description')}</span>
              </div>
              <ArrowRightIcon className="arrow-icon" />
            </div>

            <div className="settings-item" 
              onClick={this._onNavigateToDonateNow}
              onKeyDown={(e) => this._onKeyDown(e, this._onNavigateToDonateNow)}
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              tabIndex={0}
              role="button"
              aria-label={t('settings-screen:section.header.item.donate-now')}
            >
              <div className="item-left">
                <span className="item-text">{t('settings-screen:section.header.item.donate-now')}</span>
              </div>
              <ArrowRightIcon className="arrow-icon" />
            </div>
            
            <div className="settings-item" 
              onClick={this._onNavigateToFacebook}
              onKeyDown={(e) => this._onKeyDown(e, this._onNavigateToFacebook)}
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              tabIndex={0}
              role="button"
              aria-label={t('settings-screen:section.header.item.follow-us-on-facebook')}
            >
              <div className="item-left">
                <span className="item-text">{t('settings-screen:section.header.item.follow-us-on-facebook')}</span>
              </div>
              <ArrowRightIcon className="arrow-icon" />
            </div>

            <div className="settings-item" 
              onClick={this._onNavigateToContactUs}
              onKeyDown={(e) => this._onKeyDown(e, this._onNavigateToContactUs)}
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              tabIndex={0}
              role="button"
              aria-label={t('settings-screen:section.header.item.contact-us')}
            >
              <div className="item-left">
                <span className="item-text">{t('settings-screen:section.header.item.contact-us')}</span>
              </div>
              <ArrowRightIcon className="arrow-icon" />
            </div>
          </div>
          <div className="settings-section">
            <h2 className="section-title var-tag">{ process.env.REACT_APP_VERSION }</h2>
          </div>
        </div>

        {/* Loading State */}
        {this._renderLoadingSpinner()}
      </div>
    );
  }
}

// Redux connection
const mapStateToProps = (state: any) => {
  // Map server notification keys to frontend keys
  const serverSettings = state.getMe?.settings;
  let notificationSettings = null;
  
  if (serverSettings) {
    // Map server keys (goal_notifications, announcement_notifications, appointment_notifications)
    // to frontend keys (goals, announcements, appointments)
    // Don't default to true - use the actual server values
    notificationSettings = {
      goals: serverSettings.goal_notifications !== undefined ? serverSettings.goal_notifications : true,
      announcements: serverSettings.announcement_notifications !== undefined ? serverSettings.announcement_notifications : true,
      appointments: serverSettings.appointment_notifications !== undefined ? serverSettings.appointment_notifications : true,
    };
  }
  
  // Get current language from Redux, with fallback to i18n current language
  const currentLanguage = state.getMe?.locale || i18n.language || 'en';
  
  return {
    // User data
    userData: state.getMe?.data,
    currentLanguage: (currentLanguage == 'hk' ? 'zh' : currentLanguage),
    notificationSettings: notificationSettings,
    children: state.getChildren?.data,
    
    // Loading states
    getMeFetching: state.getMe?.fetching,
    getMeSuccess: state.getMe?.success,
    getMeError: state.getMe?.error,
    updateLocaleFetching: state.updateUserLocale?.fetching,
    updateNotificationFetching: state.updateUserNotificationSettings?.fetching,
    logoutFetching: state.logout?.fetching,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  getMe: (accessToken: string, navigate: any) => dispatch(GetMeActions.getMeRequest(accessToken, navigate)),
  updateUserLocale: (locale: string) => dispatch(UpdateUserLocaleActions.updateUserLocaleRequest(locale)),
  updateUserNotificationSettings: (notificationType: string, status: boolean) => 
    dispatch(UpdateUserNotificationSettingsActions.updateUserNotificationSettingsRequest(notificationType, status)),
  logout: () => dispatch(LogoutActions.logoutRequest()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(SettingsPage));

